import React, { useState } from 'react';
import '../styles/Booking.css';

const BookingSystem = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedActivity, setSelectedActivity] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const [selectedPayment, setSelectedPayment] = useState('');
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Sample data
    const activities = [
        { id: 1, name: 'Football', photo: '/assets/booking/Football.png' },
        { id: 2, name: 'Cricket', photo: '/assets/booking/Cricket.png' },
        { id: 3, name: 'Training', photo: '/assets/booking/Training.png' }
    ];

    const times = [
        { id: 1, time: '09:00 AM', available: 'Another' },
        { id: 2, time: '11:00 AM', available: 'Another' },
        { id: 3, time: '01:00 PM', available: 'Another' },
        { id: 4, time: '03:00 PM', available: 'Another' },
        { id: 5, time: '05:00 PM', available: 'Stones' },
        { id: 6, time: '07:00 PM', available: 'Another' },
        { id: 7, time: '09:00 PM', available: 'Stones' },
        { id: 8, time: '11:00 PM', available: 'Another' }
    ];

    const paymentMethods = [
        { id: 1, name: 'QR Code payment' },
        { id: 2, name: 'Net Banking' },
        { id: 3, name: 'Wallets' }
    ];

    // Calendar functions
    const getDaysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const generateCalendar = () => {
        const daysInMonth = getDaysInMonth(currentMonth);
        const firstDay = getFirstDayOfMonth(currentMonth);
        const calendar = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            calendar.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            calendar.push({
                day,
                date: dateStr,
                isToday: isToday(day),
                isSelected: selectedDate === dateStr
            });
        }

        return calendar;
    };

    const isToday = (day) => {
        const today = new Date();
        return (
            day === today.getDate() &&
            currentMonth.getMonth() === today.getMonth() &&
            currentMonth.getFullYear() === today.getFullYear()
        );
    };

    const navigateMonth = (direction) => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleContinue = () => {
        if (currentPage < 4) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleBack = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleBook = () => {
        if (currentPage === 4) {
            setCurrentPage(5); // Success page
        } else {
            handleContinue();
        }
    };

    const resetBooking = () => {
        setCurrentPage(1);
        setSelectedDate('');
        setSelectedActivity('');
        setSelectedTime('');
        setSelectedPayment('');
        setCurrentMonth(new Date());
    };

    const renderProgressBar = () => (
        <div className="progress-container">
            <div className="progress-line">
                <div
                    className="progress-fill"
                    style={{ width: `${(currentPage - 1) * 33.33}%` }}
                ></div>
            </div>
            {[1, 2, 3, 4].map((page) => (
                <div
                    key={page}
                    className={`progress-step ${currentPage >= page ? 'active' : ''} ${currentPage === page ? 'current' : ''}`}
                    onClick={() => currentPage >= page && setCurrentPage(page)}
                >
                    <div className="step-number">{page}</div>
                    <div className="step-label">
                        {page === 1 && 'Date'}
                        {page === 2 && 'Activity'}
                        {page === 3 && 'Time'}
                        {page === 4 && 'Payment'}
                    </div>
                </div>
            ))}
        </div>
    );

    // Page 1: Select Date with Calendar
    const renderDateSelection = () => {
        const calendar = generateCalendar();
        const monthNames = ["January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];

        return (
            <div className="page">
                <div className='ion'>
                    <div className='iconimages'><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M224 64C206.3 64 192 78.3 192 96L192 128L160 128C124.7 128 96 156.7 96 192L96 240L544 240L544 192C544 156.7 515.3 128 480 128L448 128L448 96C448 78.3 433.7 64 416 64C398.3 64 384 78.3 384 96L384 128L256 128L256 96C256 78.3 241.7 64 224 64zM96 288L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 288L96 288z" /></svg></div>
                    <h2 className=''> Select Date</h2>
                </div>

                <div className="calendar-container">
                    <div className="calendar-header">
                        <button className="nav-button" onClick={() => navigateMonth(-1)}>
                            ‹
                        </button>
                        <h3>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
                        <button className="nav-button" onClick={() => navigateMonth(1)}>
                            ›
                        </button>
                    </div>

                    <div className="calendar-grid">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="calendar-day-header">{day}</div>
                        ))}

                        {calendar.map((day, index) => (
                            <div
                                key={index}
                                className={`calendar-day ${day ? '' : 'empty'} ${day?.isToday ? 'today' : ''} ${day?.isSelected ? 'selected' : ''}`}
                                onClick={() => day && handleDateSelect(day.date)}
                            >
                                {day ? day.day : ''}
                            </div>
                        ))}
                    </div>

                    {selectedDate && (
                        <div className="selected-date-display">
                            Selected: <strong>{formatDate(selectedDate)}</strong>
                        </div>
                    )}
                </div>

                <button
                    className="continue-btn"
                    onClick={handleContinue}
                    disabled={!selectedDate}
                >
                    Continue
                </button>
            </div>
        );
    };

    // Page 2: Select Activity
    const renderActivitySelection = () => (
        <div className="page">
            <div className='ion'>
                <div className='iconimages'><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M128 128C128 110.3 113.7 96 96 96C78.3 96 64 110.3 64 128L64 464C64 508.2 99.8 544 144 544L544 544C561.7 544 576 529.7 576 512C576 494.3 561.7 480 544 480L144 480C135.2 480 128 472.8 128 464L128 128zM534.6 214.6C547.1 202.1 547.1 181.8 534.6 169.3C522.1 156.8 501.8 156.8 489.3 169.3L384 274.7L326.6 217.4C314.1 204.9 293.8 204.9 281.3 217.4L185.3 313.4C172.8 325.9 172.8 346.2 185.3 358.7C197.8 371.2 218.1 371.2 230.6 358.7L304 285.3L361.4 342.7C373.9 355.2 394.2 355.2 406.7 342.7L534.7 214.7z" /></svg></div>
                <h2 className=''> Select Activity</h2>
            </div>
            <div className="activity-list">
                {activities.map((activity) => (
                    <div
                        key={activity.id}
                        className={`activity-card ${selectedActivity === activity.name ? 'selected' : ''}`}
                        onClick={() => setSelectedActivity(activity.name)}
                    >
                        <img src={activity.photo} alt="" />
                        <div className="activity-name">{activity.name}</div>
                    </div>
                ))}
            </div>
            <div className="button-group">
                <button className="back-btn" onClick={handleBack}>
                    Back
                </button>
                <button
                    className="continue-btn"
                    onClick={handleContinue}
                    disabled={!selectedActivity}
                >
                    Continue
                </button>
            </div>
        </div>
    );

    // Page 3: Select Time
    const renderTimeSelection = () => (
        <div className="page">
            <div className='ion'>
                <div className='iconimages'><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64zM296 184L296 320C296 328 300 335.5 306.7 340L402.7 404C413.7 411.4 428.6 408.4 436 397.3C443.4 386.2 440.4 371.4 429.3 364L344 307.2L344 184C344 170.7 333.3 160 320 160C306.7 160 296 170.7 296 184z" /></svg></div>
                <h2 className=''> Select Time</h2>
            </div>
            <div className="time-list">
                {times.map((timeSlot) => (
                    <div
                        key={timeSlot.id}
                        className={`time-card ${selectedTime === timeSlot.time ? 'selected' : ''}`}
                        onClick={() => setSelectedTime(timeSlot.time)}
                    >
                        <div className="time-slot">{timeSlot.time}</div>
                        <div className="time-availability">{timeSlot.available}</div>
                    </div>
                ))}
            </div>
            <div className="button-group">
                <button className="back-btn" onClick={handleBack}>
                    Back
                </button>
                <button
                    className="book-btn"
                    onClick={handleBook}
                    disabled={!selectedTime}
                >
                    Proceed
                </button>
            </div>
        </div>
    );

    // Page 4: Select Payment Method
    const renderPaymentSelection = () => (
        <div className="page">
            <div className='ion'>
                <div className='iconimages'><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M64 192L64 224L576 224L576 192C576 156.7 547.3 128 512 128L128 128C92.7 128 64 156.7 64 192zM64 272L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 272L64 272zM128 424C128 410.7 138.7 400 152 400L200 400C213.3 400 224 410.7 224 424C224 437.3 213.3 448 200 448L152 448C138.7 448 128 437.3 128 424zM272 424C272 410.7 282.7 400 296 400L360 400C373.3 400 384 410.7 384 424C384 437.3 373.3 448 360 448L296 448C282.7 448 272 437.3 272 424z" /></svg></div>
                <h2 className=''> Select Payment Method</h2>
            </div>
            <div className="payment-list">
                {paymentMethods.map((method) => (
                    <div
                        key={method.id}
                        className={`payment-card ${selectedPayment === method.name ? 'selected' : ''}`}
                        onClick={() => setSelectedPayment(method.name)}
                    >
                        <div className="payment-name">{method.name}</div>
                    </div>
                ))}
            </div>
            <div className="button-group">
                <button className="back-btn" onClick={handleBack}>
                    Back
                </button>
                <button
                    className="book-btn"
                    onClick={handleBook}
                    disabled={!selectedPayment}
                >
                    Pay
                </button>
            </div>
        </div>
    );

    // Page 5: Success Page
    const renderSuccessPage = () => (
        <div className="page success-page">
            <div className="success-icon">✓</div>
            <h2>Booking Successful</h2>
            <p>Your fun has been reserved. Check your email for confirmation details.</p>
            <button className="book-another-btn" onClick={resetBooking}>
                Booking for Another Slot
            </button>
        </div>
    );

    return (
        <div className="booking-system">
            <h1 className='activitiesh1'>Book Your Turf in Minutes</h1>
            <p className='activitiesp'>Our smart booking system shows real-time availability and confirms your reservation instantly. Select your preferred date, time, and turf type to get started.</p>

            {currentPage <= 4 && renderProgressBar()}

            <div className="page-container">
                {currentPage === 1 && renderDateSelection()}
                {currentPage === 2 && renderActivitySelection()}
                {currentPage === 3 && renderTimeSelection()}
                {currentPage === 4 && renderPaymentSelection()}
                {currentPage === 5 && renderSuccessPage()}
            </div>

            {/* <div className="booking-summary">
                {selectedDate && <span>Date: {formatDate(selectedDate)}</span>}
                {selectedActivity && <span>Activity: {selectedActivity}</span>}
                {selectedTime && <span>Time: {selectedTime}</span>}
                {selectedPayment && <span>Payment: {selectedPayment}</span>}
            </div> */}
        </div>
    );
};

export default BookingSystem;